import "./SelectX.css"

import { useEffect, useRef, useState } from "react"
import classNames from "classnames"

import { hyphenate } from "web/services/inflector"
import { sortByKey } from "web/services/helpers"
import useDebouncedValue from "web/hooks/behaviour/useDebouncedValue"

import Suggestion from "./Suggestion"

export const QUICK_ADD_VALUE = "__add"

export default function Suggestions({
  suggestionIcons,
  suggestionFunction,
  suggestionDelay,
  optionsLoading,
  value,
  inputId,
  testId,
  inputValue,
  multipleValues,
  allowQuickAdd,
  includeNullSuggestion,
  sortSuggestions = true,
  onChooseSuggestion,
  suggestionComponent: SuggestionComponent = Suggestion,
}) {
  const suggestionlistId = `${inputId.current}-suggestions-list`
  const suggestionTestId = `${testId}-suggestions-list`
  const suggestionInFocus = useRef(-1)

  const [
    { loading: suggestionsLoading, suggestions: storedSuggestions },
    setSuggestionState,
  ] = useState({
    loading: null,
    suggestions: [],
  })

  const suggestionHint = useDebouncedValue(inputValue, suggestionDelay)

  useEffect(() => {
    async function getSugguestions() {
      setSuggestionState((prev) => ({
        ...prev,
        loading: true,
      }))

      const suggestions = await suggestionFunction(suggestionHint)
      setSuggestionState({
        suggestions,
        loading: false,
      })
    }

    getSugguestions()
  }, [suggestionFunction, suggestionHint])

  const normalizeValue = (val) =>
    typeof val === "string" ? val.toLowerCase() : val

  let existingValueMatch
  let suggestions = [...storedSuggestions]
  if (multipleValues) {
    const currentValues = value.map((v) => normalizeValue(v.value))
    existingValueMatch = currentValues.includes(normalizeValue(inputValue))

    suggestions = suggestions.filter(
      (suggestion) => !currentValues.includes(normalizeValue(suggestion.value))
    )
  } else {
    existingValueMatch =
      normalizeValue(value?.value) === normalizeValue(inputValue)
  }

  suggestions = sortSuggestions ? sortByKey(suggestions, "label") : suggestions

  const inputSuggestionMatch = suggestions.find(
    (s) => s.label.toLowerCase() === (inputValue || "").toLowerCase()
  )

  if (
    !inputSuggestionMatch &&
    !existingValueMatch &&
    inputValue !== "" &&
    allowQuickAdd
  ) {
    suggestions.push({
      label: `${inputValue} (Add)`,
      value: QUICK_ADD_VALUE,
    })
  }

  if (!multipleValues && includeNullSuggestion) {
    suggestions.unshift({ label: "-", value: null })
  }

  const hasSuggestions = suggestions.length !== 0
  const isLoading = optionsLoading || suggestionsLoading

  useEffect(() => {
    const suggestionsContainer = document.getElementById(suggestionlistId)

    function handleKeyDown({ detail: key }) {
      if (!["ArrowDown", "ArrowUp", "Enter"].includes(key)) return

      const suggestionDivs = suggestionsContainer.childNodes
      const hasSuggestionInFocus = suggestionInFocus.current !== -1

      if (key === "Enter" && hasSuggestionInFocus) {
        suggestionDivs[suggestionInFocus.current].click()
        return
      }

      // if key is not Enter, it must be ArrowDown or ArrowUp
      moveActiveSuggestion(suggestionDivs, key === "ArrowDown" ? 1 : -1)
    }

    function moveActiveSuggestion(suggestionDivs, direction) {
      removeActiveSuggestionStyles(suggestionDivs)

      suggestionInFocus.current += direction

      if (suggestionInFocus.current >= suggestionDivs.length) {
        suggestionInFocus.current = 0
      } else if (suggestionInFocus.current < 0) {
        suggestionInFocus.current = suggestionDivs.length - 1
      }

      suggestionDivs[suggestionInFocus.current].classList.add(
        "selectx-suggestions-active"
      )

      suggestionDivs[suggestionInFocus.current].scrollIntoView(false)
    }

    function removeActiveSuggestionStyles(suggestionDivs) {
      for (const suggestionDiv of suggestionDivs) {
        suggestionDiv.classList.remove("selectx-suggestions-active")
      }
    }

    suggestionsContainer.addEventListener("div_keydown", handleKeyDown)

    return () => {
      suggestionsContainer.removeEventListener("div_keydown", handleKeyDown)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Can't return null due to event handlers for up and down
  const listHidden = !optionsLoading && !hasSuggestions && suggestionHint === ""

  return (
    <div
      id={suggestionlistId}
      data-testid="selectx-suggestions-list"
      className={classNames("selectx-suggestions-items", {
        hidden: listHidden,
      })}
    >
      {isLoading && <div>Searching...</div>}
      {!isLoading &&
        hasSuggestions &&
        suggestions.map((suggestion) => {
          return (
            <SuggestionComponent
              testId={`${suggestionTestId}-${hyphenate(
                suggestion.label || "none"
              )}`}
              key={suggestion.key || suggestion.value}
              suggestion={suggestion}
              suggestionIcons={suggestionIcons}
              inputValue={inputValue}
              isQuickAdd={suggestion.value === QUICK_ADD_VALUE}
              onChooseSuggestion={onChooseSuggestion}
            />
          )
        })}
      {!isLoading && suggestionsLoading !== null && !hasSuggestions && (
        <div>No matching values</div>
      )}
    </div>
  )
}
